import BandCard from "../../../components/BandCard";
import Button from "../../../components/Button";
import ProgressBar from "../../../components/ProgressBar";
import { useEffect, useState } from "react";
import { useHeaderContext } from "../../../context/headerContext";
import { useLocation } from "react-router-dom";
import DropDown from "../../../components/Dropdown";
import CheckBox from "../../../components/CheckBox";
import { Link } from "react-router-dom";
import EditPencilIcon from "/public/images/icons/editPencil.svg?react";
import AddIcon from "/public/images/icons/add.svg?react";
import { useUserContext } from "../../../context/userContext";
import { useNavigate } from "react-router-dom";
import { useDrawerContext } from "../../../context/drawerContext";
import CloseIcon from "/public/images/icons/close.svg?react";
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

export default function GuestBandComplete() {
  const navigate = useNavigate();
  const { setTitle, setBackButton } = useHeaderContext();
  const { state } = useLocation();
  const { band } = state;
  const { user, logout, updateTokens } = useUserContext();
  const { setOpen, setDrawerContent } = useDrawerContext();
  const [ participantID, setParticipantID ] = useState(null);
  const [ additionalICEUpdated, setAdditionalICEUpdated ] = useState(false);
  const [ medicalUpdated, setMedicalUpdated ] = useState(false);
  const [ allergiesUpdated, setAllergiesUpdated ] = useState(false);
  const [ medicationUpdated, setMedicationUpdated ] = useState(false);
  const [ errorUpdating, setErrorUpdating ] = useState(false);

  useEffect(() => {
    setTitle("Review Details");
    setBackButton(true);
  }, []);

  useEffect(() => {
    if(participantID){
      saveAllAdditionalICEContacts();
      saveAllMedicalHistory();
      saveAllAllergies();
      saveAllMedications();
    }
  }, [participantID]);

  useEffect(() => {
    if(additionalICEUpdated && medicalUpdated && allergiesUpdated && medicationUpdated && !errorUpdating) {
      setOpen(true);
      setDrawerContent(
        <div className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            BAND ACTIVATED
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
          </div>
          <div className="mt-6 flex flex-col justify-stretch gap-6">
            <h3 className="text-[24px]">
                {band?.FriendlyName} successfully assigned to {step1Answers?.participantName}.    
            </h3>
            <Button onClick={() => setOpen(false)} >
              Close
            </Button>
          </div>
        </div>
      );

      navigate(`/guest-bands/`);
      localStorage.removeItem("activeGuestBandStep1");
      localStorage.removeItem("activeGuestBandStep2");
      localStorage.removeItem("activeGuestBandStep3");
      localStorage.removeItem("activeGuestBandStep4");
    }

    if(additionalICEUpdated && medicalUpdated && allergiesUpdated && medicationUpdated && errorUpdating){
      deactivateBand();
      setOpen(true);
      setDrawerContent(
        <div className="w-full">
          <div className="flex items-center justify-between w-full">
            <br />
            <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            ERROR
            </h2>
            <CloseIcon className='cursor-pointer' onClick={() => resetData()} />
          </div>
          <div className="mt-6 flex flex-col justify-stretch gap-6">
            <h3 className="text-[24px]">
                {band?.FriendlyName} failed to be assigned to {step1Answers?.participantName}.    
            </h3>
            <Button onClick={() => resetData()} >
              Close
            </Button>
          </div>
        </div>
      );
    }
  }, [additionalICEUpdated, medicalUpdated, allergiesUpdated, medicationUpdated, errorUpdating])

  const resetData = () => {
    setOpen(false);
    setAdditionalICEUpdated(false);
    setMedicalUpdated(false);
    setAllergiesUpdated(false);
    setMedicationUpdated(false);
    setErrorUpdating(false);
  }

  const formatDate = (dateString) => {

    if (!dateString) {
      return "(not provided)";
    }

    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const step1Answers = JSON.parse(localStorage.getItem("activeGuestBandStep1"));
  const step2Answers = JSON.parse(localStorage.getItem("activeGuestBandStep2"));
  const step3Answers = JSON.parse(localStorage.getItem("activeGuestBandStep3"));
  const step4Answers = JSON.parse(localStorage.getItem("activeGuestBandStep4"));

  const deactivateBand = async () => {
    await Sentry.startSpan({ name: "Deactivate Guest Band", op: "http.request" }, async (span) => {
      try {
        const response = await fetchWithRefresh(
          `${import.meta.env.VITE_API_URL}GuestBands/DeActivateGuestBand?bandId=${band?.BandID}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        span.setAttribute("http.status_code", response.status);
  
        if (!response.ok) {
          throw new Error("Failed to deactivate band.");
        }
      } catch (error) {
        span.setStatus("error");
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, "Error deactivating band: " + error);
      } finally {
        span.end();
      }
    });
  };  

  const handleBandActivation = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
  
    await Sentry.startSpan({ name: "Activate Guest Band", op: "http.request" }, async (span) => {
      try {
        const response = await fetchWithRefresh(
          `${import.meta.env.VITE_API_URL}GuestBands/RegisterParticipantForGuestBand`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${user.token}`,
            },
            body: JSON.stringify({
              bandId: band.BandID,
              participantName: step1Answers.participantName,
              primaryMedicalCondition: step1Answers.bandPrimaryCondition,
              primaryIceNumber: step1Answers.bandICEPhoneNumber,
              primaryIceName: step1Answers.bandICEName,
              primaryIceRelationship: step1Answers.bandICERelation,
              dateOfBirth: step1Answers.participantDOB,
              notes: step1Answers.participantNotes,
            }),
          },
          logout,
          updateTokens,
          (message) => { toast.info(message); }
        );
  
        span.setAttribute("http.status_code", response.status);
  
        if (response.ok) {
          const data = await response.json();
          setParticipantID(data[0]?.ParticipantId);
        } else {
          span.setStatus("error");

        // Extract error message from response
        let errorMessage = "An unexpected error occurred."; // Default message
        
        try {
          errorMessage = await response.text(); // Get the raw text response

          // If the response text is empty, set a fallback message
          if (!errorMessage.trim()) {
              errorMessage = "An unexpected error occurred.";
          }
        } catch (parseError) {
          console.error("Failed to retrieve error message:", parseError);
        }


        // Show the error in the drawer
        setOpen(true);
        setDrawerContent(
            <div className="w-full">
                <div className="flex items-center justify-between w-full">
                    <br />
                    <h2 className="leading-5 tracking-widest uppercase text-[12px]">
                        BAND ACTIVATION FAILED
                    </h2>
                    <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
                </div>
                <div className="mt-6 flex flex-col justify-stretch gap-6">
                    <h3 className="text-[24px]">
                        {errorMessage}    
                    </h3>
                    <Button onClick={() => setOpen(false)}>Close</Button>
                </div>
            </div>
        );

          throw new Error("Failed to save user's profile.");
        }
      } catch (error) {
        span.setStatus("error");
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, "Error saving user's profile: " + error);
      } finally {
        span.end();
      }
    });
  };  
    
  // Save Additional ICE Contacts
  const saveAllAdditionalICEContacts = async () => {
    await Sentry.startSpan({ name: "Save Additional ICE Contacts", op: "http.request" }, async (span) => {
      try {
        const promises = step1Answers.additionalICEContacts.map((contact) =>
          fetchWithRefresh(
            import.meta.env.VITE_API_URL + "Profile/SaveAdditionalICEContact",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                participantId: participantID,
                additionalICEName: contact.name,
                additionalICENumber: contact.phoneNumber,
                additionalICERelationship: contact.relation,
                emergencySMS: 0,
              }),
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          )
          .then((response) => {
            span.setAttribute("http.status_code", response.status);
            return { contactName: contact.name, ok: response.ok };
          })
          .catch((error) => {
            Sentry.captureException(error);
            return { contactName: contact.name, ok: false, error };
          })
        );
  
        const results = await Promise.all(promises);
  
        let errorFound = false;
        results.forEach((result) => {
          if (!result.ok) {
            LogErrors(user, logout, updateTokens, `Failed to save ICE contact: ${result.contactName}`);
            errorFound = true;
          }
        });
  
        if (errorFound) {
          span.setStatus("error");
          setErrorUpdating(true);
        } else {
          setAdditionalICEUpdated(true);
        }
      } catch (error) {
        span.setStatus("error");
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, "Error saving additional ICE contacts: " + error);
      } finally {
        span.end();
      }
    });
  };  

  // Save Medical History
  const saveAllMedicalHistory = async () => {
    await Sentry.startSpan({ name: "Save Medical History", op: "http.request" }, async (span) => {
      try {
        const promises = step2Answers.map((history) =>
          fetchWithRefresh(
            import.meta.env.VITE_API_URL + "Profile/SaveMedicalHistoryItem",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                participantId: participantID,
                conditionName: history.name,
                notes: history.additionalInformation,
                ongoing: history.ongoing ? 1 : 0,
              }),
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          )
          .then((response) => {
            span.setAttribute("http.status_code", response.status);
            return { historyName: history.name, ok: response.ok };
          })
          .catch((error) => {
            Sentry.captureException(error);
            return { historyName: history.name, ok: false, error };
          })
        );
  
        const results = await Promise.all(promises);
  
        let errorFound = false;
        results.forEach((result) => {
          if (!result.ok) {
            LogErrors(user, logout, updateTokens, `Failed to save medical history: ${result.historyName}`);
            errorFound = true;
          }
        });
  
        if (errorFound) {
          span.setStatus("error");
          setErrorUpdating(true);
        } else {
          setMedicalUpdated(true);
        }
      } catch (error) {
        span.setStatus("error");
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, "Error saving medical history: " + error);
      } finally {
        span.end();
      }
    });
  };  

  // Save Allergies
  const saveAllAllergies = async () => {
    await Sentry.startSpan({ name: "Save Allergies", op: "http.request" }, async (span) => {
      try {
        const promises = step3Answers.map((allergy) =>
          fetchWithRefresh(
            import.meta.env.VITE_API_URL + "Profile/SaveAllergy",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                participantId: participantID,
                conditionName: allergy.name,
                notes: allergy.additionalInformation,
                ongoing: allergy.ongoing ? 1 : 0,
              }),
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          )
          .then((response) => {
            span.setAttribute("http.status_code", response.status);
            return { allergyName: allergy.name, ok: response.ok };
          })
          .catch((error) => {
            Sentry.captureException(error);
            return { allergyName: allergy.name, ok: false, error };
          })
        );
  
        const results = await Promise.all(promises);
  
        let errorFound = false;
        results.forEach((result) => {
          if (!result.ok) {
            LogErrors(user, logout, updateTokens, `Failed to save allergy: ${result.allergyName}`);
            errorFound = true;
          }
        });
  
        if (errorFound) {
          span.setStatus("error");
          setErrorUpdating(true);
        } else {
          setAllergiesUpdated(true);
        }
      } catch (error) {
        span.setStatus("error");
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, "Error saving allergies: " + error);
      } finally {
        span.end();
      }
    });
  };  

  // Save Medications
  const saveAllMedications = async () => {
    await Sentry.startSpan({ name: "Save Medications", op: "http.request" }, async (span) => {
      try {
        const promises = step4Answers.map((medication) =>
          fetchWithRefresh(
            import.meta.env.VITE_API_URL + "Profile/SaveMedication",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user.token}`,
              },
              body: JSON.stringify({
                participantId: participantID,
                medicationName: medication.medicationName,
                dosage: medication.medicationDosage,
                frequency: medication.medicationFrequency,
                ongoing: medication.ongoing ? 1 : 0,
                notes: medication.additionalInformation || "",
              }),
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          )
          .then((response) => {
            span.setAttribute("http.status_code", response.status);
            return { medicationName: medication.medicationName, ok: response.ok };
          })
          .catch((error) => {
            Sentry.captureException(error);
            return { medicationName: medication.medicationName, ok: false, error };
          })
        );
  
        const results = await Promise.all(promises);
  
        let errorFound = false;
        results.forEach((result) => {
          if (!result.ok) {
            LogErrors(user, logout, updateTokens, `Failed to save medication: ${result.medicationName}`);
            errorFound = true;
          }
        });
  
        if (errorFound) {
          span.setStatus("error");
          setErrorUpdating(true);
        } else {
          setMedicationUpdated(true);
        }
      } catch (error) {
        span.setStatus("error");
        Sentry.captureException(error);
        LogErrors(user, logout, updateTokens, "Error saving medications: " + error);
      } finally {
        span.end();
      }
    });
  };  

  // If all API calls succeeded, activate the band
  // const activateGuestBand = async () => {
  //   try {
  //     const activateBandResponse = await fetchWithRefresh(import.meta.env.VITE_API_URL + `Bands/ActivateBand/?bandId=${band.BandID}&userTriggering=${user.id}`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //         Authorization: `Bearer ${user.token}`,
  //       },
  //     });

  //     if (!activateBandResponse.ok) {
  //       LogErrors(user, logout, updateTokens, "Failed to activate band.");
  //     } else {
        
  //     }
  //   } catch (error) {
  //     LogErrors(user, logout, updateTokens, "Error activating band:", error);
  //   }
  // }

  return (
    <div className="w-full flex flex-col justify-stretch mb-[80px]">
      <div className="p-[24px] flex flex-col gap-14">
        <ProgressBar step={4} overrideTotalSteps={4} complete />

        <BandCard
          size="sm"
          band={band}
          participant={band}
        />

        <div className="flex flex-col justify-stretch gap-4">
          <h3>{step1Answers.participantName}</h3>
          <span className="text-sportsGrey font-poppins ">
            Last Updated • {new Date().toLocaleDateString("en-gb")}
          </span>
          <div className="flex flex-col border-t border-dashed border-[#E3E3E3] pt-6 !mt-6" />
          <p className="font-semibold">Date of Birth</p>
          <p className="text-sportsGrey">{formatDate(step1Answers?.participantDOB)}</p>

          <p className="font-semibold">Primary Emergency Contact Name (ICE)</p>
          <p className="text-sportsGrey">{step1Answers?.bandICEName?.trim() ? step1Answers.bandICEName : "(not provided)"}</p>

          <p className="font-semibold">Primary Emergency Contact Number</p>
          <p className="text-sportsGrey">{step1Answers?.bandICEPhoneNumber}</p>

          <p className="font-semibold">Primary Emergency Contact Relationship</p>
          <p className="text-sportsGrey">{step1Answers?.bandICERelation.trim() ? step1Answers.bandICERelation : "(not provided)"}</p>

          <p className="font-semibold">Primary Medical Condition</p>
          <p className="text-sportsGrey">{step1Answers?.bandPrimaryCondition}</p>

          {step1Answers?.participantNotes && (
            <>
              <p className="font-semibold">Notes</p>
              <p className="text-sportsGrey">{step1Answers?.participantNotes}</p>
            </>
          )}
        </div>
      </div>
      <div className="bg-sportsLightGrey border-[#E3E3E3] border-t-[1px] p-[24px] flex justify-center flex-col gap-6">
        
        {step2Answers.length > 0 && (
          <DropDown title="Medical History" icon="medical">
            <div className='flex flex-col gap-y-[15px]'>
              {step2Answers?.map((condition, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${step2Answers.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Condition Name</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{condition.name}</p>
                      <p className='font-poppins text-[12px] leading-[12px] text-sportsGrey'>Date Added • {formatDate(condition.dateAdded)}</p>
                    </div>
                    <Link to={`/guest-bands/${band?.BandID}/activate/2`} state={{band: band}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Ongoing</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{condition.ongoing ? "Yes" : "No"}</p>
                    </div>
                    <Link to={`/guest-bands/${band?.BandID}/activate/3`} state={{band: band}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  {condition.additionalInformation && (
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-[6px]">
                        <p className='font-poppins text-[16px] font-medium leading-[26px]'>Additional Information</p>
                        <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{condition.additionalInformation}</p>
                      </div>
                      <Link to={`/guest-bands/${band?.BandID}/activate/3`} state={{band: band}}>
                        <EditPencilIcon />
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DropDown>
        )}

        {step3Answers.length > 0 && (
          <DropDown title="Allergies" icon="allergy">
            <div className='flex flex-col gap-y-[15px]'>
              {step3Answers?.map((allergy, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${step3Answers.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Allergy Name</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{allergy.name}</p>
                    </div>
                    <Link to={`/guest-bands/${band?.BandID}/activate/3`} state={{band: band}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  {allergy.additionalInformation && (
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-[6px]">
                        <p className='font-poppins text-[16px] font-medium leading-[26px]'>Additional Information</p>
                        <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{allergy.additionalInformation}</p>
                      </div>
                      <Link to={`/guest-bands/${band?.BandID}/activate/3`} state={{band: band}}>
                        <EditPencilIcon />
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DropDown>
        )}

        {step4Answers.length > 0 && (
          <DropDown title="Medications" icon="medication">
            <div className='flex flex-col gap-y-[15px]'>
              {step4Answers?.map((medication, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${step4Answers.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Medication Name</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication.medicationName}</p>
                    </div>
                    <Link to={`/guest-bands/${band?.BandID}/activate/4`} state={{band: band}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Dosage</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication.medicationDosage}</p>
                    </div>
                    <Link to={`guest-bands/${band?.BandID}/activate/4`} state={{band: band}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Frequency</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication.medicationFrequency}</p>
                    </div>
                    <Link to={`/guest-bands/${band?.BandID}/activate/4`} state={{band: band}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  {medication.additionalInformation && (
                    <div className="flex justify-between items-center">
                      <div className="flex flex-col gap-[6px]">
                        <p className='font-poppins text-[16px] font-medium leading-[26px]'>Additional Information</p>
                        <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{medication.additionalInformation}</p>
                      </div>
                      <Link to={`/guest-bands/${band?.BandID}/activate/4`} state={{band: band}}>
                        <EditPencilIcon />
                      </Link>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </DropDown>
        )}

        {step1Answers?.additionalICEContacts.length > 0 && (
          <DropDown title="Other Emergency Contacts" icon="contact">
            <div className='flex flex-col gap-y-[15px]'>
              {step1Answers?.additionalICEContacts?.map((contact, index) => (
                <div key={index} className={`flex flex-col gap-[30px] ${step1Answers?.additionalICEContacts.length === (index + 1) && "!border-transparent"} border-dashed border-b border-[#E3E3E3] pb-[15px]`}>
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Emergency Contact {index + 1}</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{contact?.name} - {contact?.phoneNumber}</p>
                    </div>
                    <Link to={`/guest-bands/${band?.BandID}/activate/1`} state={{band: band}}>
                      <EditPencilIcon />
                    </Link>
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="flex flex-col gap-[6px]">
                      <p className='font-poppins text-[16px] font-medium leading-[26px]'>Relationship</p>
                      <p className='font-poppins text-[16px] leading-[26px] text-sportsGrey'>{contact?.relation}</p>
                    </div>
                    <Link to={`/guest-bands/${band?.BandID}/activate/1`} state={{band: band}}>
                      <EditPencilIcon />
                    </Link>
                  </div>
                </div>
              ))}

              {step1Answers?.additionalICEContacts.length < band?.GuestBandICEContactLimit && (
                <Link to={`/guest-bands/${band?.BandID}/activate/1`} state={{band: band}} className="mx-auto">
                    <Button variant="tertiary"><AddIcon className="w-4 h-4"/> Add</Button>
                </Link>
              )}
            </div>
          </DropDown>
        )}

        <form onSubmit={handleBandActivation} className="flex flex-col gap-[30px]">
          <div className="font-semibold flex gap-5 pr-[20px]">
            <CheckBox 
              name="confirmation" 
              onInput={(e) => e.target.setCustomValidity("")}
              onInvalid={(e) => e.target.setCustomValidity('Our partners rely on this information to assist you in the event of an emergency. Please confirm your details are complete and up to date.')}
              required
            />  I confirm all the information provided is correct and up to date
          </div>
          <Button submit={true}>Activate Band</Button>
        </form>
      </div>
    </div>
  );
}
